import { useAuthentication, useLogging } from '@ecosystem/newron-client-lib';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Socket, io } from 'socket.io-client';

interface SocketContextType {
  socket: Socket | null;
  disconnect: () => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

export const useSocket = (): SocketContextType => {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return ctx;
};

interface SocketProviderProps {
  children: React.ReactNode;
  path: string;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, path }) => {
  const { getAccessToken } = useAuthentication();
  const { info: logInfo, error: logError } = useLogging();

  const [socket, setSocket] = useState<Socket | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const eventsRef = useRef<{ event: string; sid?: string; reason?: string; timestamp: string; message?: string }[]>([]);
  const lastSidRef = useRef<string | undefined>(undefined);

  const disconnect = useCallback(() => {
    if (!socketRef.current) {
      return;
    }
    socketRef.current.removeAllListeners();
    socketRef.current.disconnect();
    socketRef.current = null;
    setSocket(null);
  }, []);

  useEffect(() => {
    const flushInterval = setInterval(() => {
      if (eventsRef.current.length === 0) {
        return;
      }

      logInfo('Socket Provider - Summary', {
        count: eventsRef.current.length,
        events: eventsRef.current as unknown as string,
        path,
        summaryCapturedAt: new Date().toLocaleString(),
      });
      eventsRef.current = [];
    }, 10 * 60 * 1000);

    return () => clearInterval(flushInterval);
  }, [logInfo, path]);

  useEffect(() => {
    (async () => {
      const token = await getAccessToken();
      if (socketRef.current?.connected) {
        return;
      }

      const socketInstance = io('/', {
        path: `${path}/socket.io`,
        auth: { token: `Bearer ${token}` },
        timeout: 60000,
      });

      socketRef.current = socketInstance;
      setSocket(socketInstance);

      const recordEvent = (event: string, extra?: Partial<{ reason: string; message: string }>) => {
        const sid = socketInstance?.id ?? lastSidRef.current;
        eventsRef.current.push({
          event,
          sid,
          timestamp: new Date().toLocaleString(),
          ...extra,
        });
      };

      socketInstance.on('connect', () => {
        lastSidRef.current = socketInstance.id;
        recordEvent('connect', { message: `Connected to socket with sid: ${socketInstance.id}` });
      });

      socketInstance.on('disconnect', reason =>
        recordEvent('disconnect', { message: 'Disconnected from socket', reason })
      );

      socketInstance.on('reconnect_attempt', () => recordEvent('reconnect_attempt'));

      socketInstance.on('connect_error', err => {
        logError({
          name: 'connect_error',
          message: `Failed to connect to socket: ${err.message ?? 'Unknown error'}`,
          stack: err.stack,
        });
      });

      socketInstance.on('error', err => {
        logError({
          name: 'socket_error',
          message: `Socket encountered an error: ${err.message ?? 'Unknown error'}`,
          stack: err.stack,
        });
      });
    })();

    return () => {
      if (eventsRef.current.length > 0) {
        logInfo('Socket Provider - Unmount', {
          count: eventsRef.current.length,
          events: eventsRef.current as unknown as string,
          path,
          summaryCapturedAt: new Date().toLocaleString(),
        });
        eventsRef.current = [];
      }
      disconnect();
    };
  }, [getAccessToken, path, logError, logInfo, disconnect]);

  const value = useMemo(() => ({ socket, disconnect }), [socket, disconnect]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
